import { initial, last } from 'lodash';

export type MatchSegments = Array<{
  text: string;
  matched: boolean;
}>;

/**
 * Given a result of running a regex against a string (either null or `RegExpExecArray`)
 * Return null if there was not a match
 * Otherwise, return an array that splits the original string that was being matched
 * on into segments, with each segment having a property `matched` that indicates
 * whether this segment was part of the match (and should probably be specially formatted)
 */
export const explainRegexMatch = (
  regExpExecResult: ReturnType<RegExp['exec']>,
): MatchSegments | null => {
  if (!regExpExecResult) {
    return null;
  }
  const originalString = regExpExecResult.input;

  // This index is used to keep track of what part of the originString has already
  // been consumed. This avoid repeatedly matching on the same segment for words
  // containing repetition
  let indexInString = 0;
  const parenthesizedSubstringMatches = regExpExecResult.slice(1);

  if (!parenthesizedSubstringMatches.length) {
    return [
      {
        text: regExpExecResult.input,
        matched: false,
      },
    ];
  }

  const segments: MatchSegments = [];

  parenthesizedSubstringMatches.forEach((currentMatch, indexInMatches) => {
    const remainingString = originalString.slice(indexInString);
    const indexOfCurrentMatchInRemainingString =
      remainingString.indexOf(currentMatch);
    if (indexOfCurrentMatchInRemainingString > 0) {
      // there are characters between where the current index is and where the
      // current match is. push this as an unmatched segment
      const beforeMatch = remainingString.slice(
        0,
        indexOfCurrentMatchInRemainingString,
      );
      segments.push({
        text: beforeMatch,
        matched: false,
      });
      indexInString += beforeMatch.length;
    }

    // push the match into segments
    segments.push({
      text: currentMatch,
      matched: true,
    });
    indexInString += currentMatch.length;

    if (
      indexInMatches === parenthesizedSubstringMatches.length - 1 &&
      indexInString < originalString.length
    ) {
      // there are characters between "the last matching segment" and "the end of the string"
      // push this as an unmatched segment
      const afterMatch = originalString.slice(indexInString);
      segments.push({
        text: afterMatch,
        matched: false,
      });
      indexInString += afterMatch.length;
    }
  });

  return compactSegments(segments);
};

/**
 * Compbines consecutive segments with the same `matched` value
 * Given [{text: 'a', matched: true}, {text: 'b', matched: true}], return [{text: 'ab', matched: true}]
 */
const compactSegments = (segments: MatchSegments) => {
  return segments.reduce((compactedSegments: MatchSegments, nextSegment) => {
    const lastCompactedSegment = last(compactedSegments);
    if (lastCompactedSegment?.matched === nextSegment.matched) {
      return initial(compactedSegments).concat({
        text: lastCompactedSegment.text + nextSegment.text,
        matched: lastCompactedSegment.matched,
      });
    }
    return compactedSegments.concat(nextSegment);
  }, []);
};
